import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Eye, MousePointerClick, UserCheck, Clock, RefreshCcw, AlertTriangle } from "lucide-react";
import { InviteDialog } from "@/components/InviteDialog";

const relTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

const StatusPill = ({ label, ts, color, Icon }) => {
  if (!ts) return (
    <span className="inline-flex items-center gap-1 text-[10px] text-slate-600 font-mono uppercase">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-700" />
      {label}
    </span>
  );
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase"
      style={{ color }}
      title={new Date(ts).toLocaleString()}
    >
      <Icon className="w-3 h-3" /> {label} · {relTime(ts)}
    </span>
  );
};

export const InvitesPanel = ({ jobId }) => {
  const [invites, setInvites] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgeTarget, setNudgeTarget] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/invites/job/${jobId}`)
      .then((r) => setInvites(r.data))
      .catch(() => setInvites([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (jobId) load(); }, [jobId]);

  if (loading) return <div className="text-slate-500 text-sm py-6 text-center">Loading invites…</div>;
  if (!invites || invites.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500 text-sm">
        <Mail className="w-5 h-5 mx-auto mb-2 text-slate-600" />
        No invites sent yet. Use <span className="text-[#06B6D4]">Bulk score resumes</span> to invite top matches.
      </div>
    );
  }

  // Sort: nudge-needed first, then most recent.
  const sorted = [...invites].sort((a, b) => {
    if (a.needs_nudge && !b.needs_nudge) return -1;
    if (!a.needs_nudge && b.needs_nudge) return 1;
    return (b.sent_at || "").localeCompare(a.sent_at || "");
  });
  const nudgeCount = invites.filter((i) => i.needs_nudge).length;

  return (
    <div data-testid="invites-panel">
      <InviteDialog
        open={!!nudgeTarget}
        onOpenChange={(o) => !o && setNudgeTarget(null)}
        jobId={jobId}
        candidate={nudgeTarget ? { email: nudgeTarget.email, name: nudgeTarget.name } : null}
        initialTemplate="nudge"
        onSent={load}
      />

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500 font-mono uppercase flex items-center gap-3">
          <span>{invites.length} invite{invites.length === 1 ? "" : "s"} sent</span>
          {nudgeCount > 0 && (
            <span className="text-[#F59E0B] flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {nudgeCount} needs nudge
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="invites-refresh-btn"
          className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
        >
          <RefreshCcw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="border border-white/10 rounded-xl overflow-hidden">
        {sorted.map((inv, idx) => (
          <div
            key={inv.invite_id}
            data-testid={`invite-row-${inv.invite_id}`}
            className={`px-4 py-3 grid grid-cols-12 gap-3 items-center ${
              idx !== sorted.length - 1 ? "border-b border-white/5" : ""
            } ${inv.needs_nudge ? "bg-[#F59E0B]/5" : ""}`}
          >
            <div className="col-span-4 min-w-0">
              <div className="text-sm text-slate-200 truncate flex items-center gap-2">
                {inv.name || <span className="text-slate-400">Unnamed</span>}
                {inv.needs_nudge && (
                  <span
                    className="text-[9px] px-1.5 py-0.5 rounded border border-[#F59E0B]/40 text-[#F59E0B] font-mono uppercase"
                    title={`Sent ${inv.age_days} days ago, no open`}
                  >
                    needs nudge
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 truncate">{inv.email}</div>
            </div>
            <div className="col-span-6 flex flex-wrap gap-x-4 gap-y-1.5">
              <StatusPill label="Sent" ts={inv.sent_at} color="#3B82F6" Icon={Clock} />
              <StatusPill label="Opened" ts={inv.opened_at} color="#06B6D4" Icon={Eye} />
              <StatusPill label="Clicked" ts={inv.clicked_at} color="#F59E0B" Icon={MousePointerClick} />
              <StatusPill label="Signed up" ts={inv.signed_up_at} color="#10B981" Icon={UserCheck} />
            </div>
            <div className="col-span-2 flex justify-end items-center gap-2">
              {inv.needs_nudge && (
                <button
                  type="button"
                  onClick={() => setNudgeTarget(inv)}
                  data-testid={`invite-nudge-${inv.invite_id}`}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 flex items-center gap-1"
                >
                  <Mail className="w-3 h-3" /> Nudge
                </button>
              )}
              {inv.resend_count > 0 && (
                <span className="text-[10px] text-slate-500 font-mono uppercase" title="Times resent">
                  x{inv.resend_count + 1}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
